import { useCallback, useEffect, useState } from "react";
import {
  getRoadmaps,
  getRoadmapStages,
  getMyRoadmapProgress,
  startMyRoadmapProgress,
} from "../../repository/data.repository";
import { MAX_PAGE_SIZE } from "../../repository/data.default";
import { QueryOperation } from "../../code/search-query/search-query";

export interface ProcessItem {
  Title: string;
  // The stage's "Steps" as rich HTML — rendered directly as the journey-map highlights.
  steps: string;
  // The stage's rich description (HTML) — drives the overview summary text.
  overview: string;
  isCompleted: boolean;
  Level: number;
  Stage: string;
  componentUrl: string;
}

export interface RoadmapContext {
  uid: string;
  name: string;
  description: string;
  currentStage: number;
  currentRequirement: number;
  totalStages: number;
}

// Drives the company-app roadmap overview off the single admin-authored roadmap.
// Stage content is global (same for every company); progress is this company's own
// RoadmapCompany row (via the self-scoped endpoint) — absent => not started.
export const useProcessItems = () => {
  const [processItems, setProcessItems] = useState<ProcessItem[]>([]);
  const [roadmap, setRoadmap] = useState<RoadmapContext | null>(null);
  const [notStarted, setNotStarted] = useState(false);
  const [noRoadmap, setNoRoadmap] = useState(false);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // `silent` skips the full-screen "Loading…" state so a post-start refresh updates the map
  // in place (no blank flash) — letting the start transition animate smoothly.
  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    return getRoadmaps({
      searchQuery: { conditions: [] },
      pageNumber: 0,
      pageSize: 1,
      sortBy: "createdOn",
      sortDescending: false,
    })
      .then(async (roadmapsData) => {
        const first = roadmapsData.data[0];
        if (!first) {
          setNoRoadmap(true);
          setNotStarted(false);
          setProcessItems([]);
          setRoadmap(null);
          setLoading(false);
          return;
        }

        const [stagesData, progress] = await Promise.all([
          getRoadmapStages({
            searchQuery: {
              conditions: [
                { field: "roadmapUid", values: [first.uid], operation: QueryOperation.Equal },
              ],
            },
            pageNumber: 0,
            pageSize: MAX_PAGE_SIZE,
            sortBy: "order",
            sortDescending: false,
          }),
          getMyRoadmapProgress(first.uid),
        ]);

        // No progress row => not started; use stage 0 so nothing reads as current/complete yet.
        const currentStage = progress?.currentStage ?? 0;
        const currentRequirement = progress?.currentRequirement ?? 0;
        const stages = stagesData.data;

        setNoRoadmap(false);
        setNotStarted(progress == null);
        setProcessItems(
          stages.map((s) => ({
            Title: s.name,
            // Map highlights = the stage's Steps (rendered as authored HTML).
            steps: s.stepsDescription,
            // Overview summary text = the stage's rich description.
            overview: s.description,
            isCompleted: s.order < currentStage,
            Level: s.order,
            Stage: s.name,
            componentUrl: `/stage/${s.uid}`,
          })),
        );
        setRoadmap({
          uid: first.uid,
          name: first.name,
          description: first.description,
          currentStage,
          currentRequirement,
          totalStages: stages.length,
        });
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Enroll this company in the journey (idempotent), then refresh.
  const start = useCallback(() => {
    if (!roadmap) return Promise.resolve();
    setStarting(true);
    return startMyRoadmapProgress(roadmap.uid)
      .then(() => load(true)) // silent — keep the map mounted so the transition can animate
      .finally(() => setStarting(false));
  }, [roadmap, load]);

  return { processItems, loading, error, notStarted, noRoadmap, roadmap, start, starting };
};
